import React, { useState, useEffect, useRef } from 'react';
import { Eye, Loader2, AlertTriangle } from 'lucide-react';
import type { UploadedImage, ProcessingOptions } from '../types';
import { generatePreview, isPanoramaRatio } from '../services/imageProcessor';

interface ImagePreviewProps {
  images: UploadedImage[];
  options: ProcessingOptions;
}

const ImagePreview: React.FC<ImagePreviewProps> = ({ images, options }) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  const selectedImage = images[selectedIndex] || null;
  const showWatermarkPreview = options.watermark.enabled && options.watermark.logoFile;
  const isTour360 = options.resize.mode === 'tour360';
  const nonPanoramaCount = isTour360
    ? images.filter((img) => !isPanoramaRatio(img.originalWidth, img.originalHeight)).length
    : 0;
  const selectedIsPanorama = selectedImage
    ? isPanoramaRatio(selectedImage.originalWidth, selectedImage.originalHeight)
    : true;

  useEffect(() => {
    if (selectedIndex >= images.length) {
      setSelectedIndex(Math.max(0, images.length - 1));
    }
  }, [images.length, selectedIndex]);

  useEffect(() => {
    if (!selectedImage) {
      setPreviewUrl(null);
      return;
    }

    // In 360 mode we always run the previewer so quality changes are visible.
    if (!isTour360 && !showWatermarkPreview && !options.resize.targetWidth) {
      setPreviewUrl(selectedImage.previewUrl);
      return;
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const url = await generatePreview(selectedImage, options);
        setPreviewUrl(url);
      } catch {
        setPreviewUrl(selectedImage.previewUrl);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [selectedImage, options.watermark.enabled, options.watermark.logoFile, options.watermark.position, options.watermark.scale, options.watermark.opacity, options.watermark.margin, options.resize.targetWidth, options.resize.mode, options.resize.quality, showWatermarkPreview, isTour360]);

  if (images.length === 0) return null;

  return (
    <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-5">
      <div className="flex items-center gap-2.5 mb-4">
        <div className="w-8 h-8 rounded-lg bg-cyan-900/30 border border-cyan-500/20 flex items-center justify-center">
          <Eye size={16} className="text-cyan-400" />
        </div>
        <h3 className="text-sm font-semibold text-slate-200">Preview</h3>
        {loading && <Loader2 size={14} className="text-cyan-400 animate-spin ml-2" />}
      </div>

      {isTour360 && nonPanoramaCount > 0 && (
        <div className="mb-3 flex items-start gap-2 p-3 rounded-lg bg-amber-900/15 border border-amber-500/30 text-[11px] text-amber-200/90">
          <AlertTriangle size={14} className="shrink-0 mt-0.5" />
          <span>
            {nonPanoramaCount} of {images.length} image{images.length !== 1 ? 's' : ''} aren't 2:1 panoramas and will be skipped.
          </span>
        </div>
      )}

      {/* Large Preview — switches to 2:1 frame in 360 mode */}
      <div className={`relative ${isTour360 ? 'aspect-[2/1]' : 'aspect-[16/10]'} bg-slate-800 rounded-xl overflow-hidden mb-3`}>
        {previewUrl ? (
          <img src={previewUrl} alt="Preview" className="w-full h-full object-contain" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-slate-500 text-sm">
            Select an image to preview
          </div>
        )}
        {loading && (
          <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
            <Loader2 size={24} className="text-cyan-400 animate-spin" />
          </div>
        )}
      </div>

      {/* Thumbnail Selector */}
      {images.length > 1 && (
        <div className="flex gap-1.5 overflow-x-auto pb-1">
          {images.map((img, idx) => (
            <button
              key={img.id}
              onClick={() => setSelectedIndex(idx)}
              className={`w-12 h-9 shrink-0 rounded-md overflow-hidden border-2 transition-all ${
                idx === selectedIndex
                  ? 'border-cyan-500 ring-1 ring-cyan-500/30'
                  : 'border-slate-700 hover:border-slate-500'
              }`}
            >
              <img src={img.previewUrl} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}

      {/* Info */}
      {selectedImage && (
        <div className="mt-3 flex items-center gap-3 text-[11px] text-slate-500 flex-wrap">
          <span>
            Original: {selectedImage.originalWidth} &times; {selectedImage.originalHeight}
            {' '}
            <span className="text-slate-600">
              ({(selectedImage.file.size / (1024 * 1024)).toFixed(1)} MB)
            </span>
          </span>
          {isTour360 ? (
            selectedIsPanorama ? (
              <>
                <span>&rarr;</span>
                <span className="text-cyan-400">
                  {(() => {
                    const w = options.resize.targetWidth && options.resize.targetWidth < selectedImage.originalWidth
                      ? options.resize.targetWidth
                      : selectedImage.originalWidth;
                    const ew = w % 2 === 0 ? w : w - 1;
                    return `${ew} × ${ew / 2} (2:1)`;
                  })()}
                </span>
              </>
            ) : (
              <span className="text-amber-400 flex items-center gap-1">
                <AlertTriangle size={11} /> Not 2:1 — will be skipped
              </span>
            )
          ) : (
            options.resize.targetWidth && options.resize.targetWidth < selectedImage.originalWidth && (
              <>
                <span>&rarr;</span>
                <span className="text-cyan-400">
                  {options.resize.targetWidth} &times; {Math.round((selectedImage.originalHeight / selectedImage.originalWidth) * options.resize.targetWidth)}
                </span>
              </>
            )
          )}
        </div>
      )}
    </div>
  );
};

export default ImagePreview;
